<template>
  <v-list subheader class="booking-group-data-list">
    <template v-if="!hiddenSections.includes('booking-group')">
      <template v-if="isLoading">
        <div
          aria-busy="true"
          aria-live="polite"
          role="alert"
          class="v-skeleton-loader v-skeleton-loader--is-loading theme--light"
          style="max-width: 300px;"
        >
          <div class="v-skeleton-loader__list-item-avatar v-skeleton-loader__bone">
            <div class="v-skeleton-loader__avatar v-skeleton-loader__bone"></div>
            <div style="margin-top: -8px; width: 160px; max-width: 160px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
            <div style="position: absolute; left: 72px; top: 32px; width: 212px; height: 8px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <v-list-item @click.native="openBookingGroup" :ripple="bookingGroupClickable" :class="{ 'cursor-pointer highlight-on-hover': bookingGroupClickable }">
          <v-list-item-icon style="margin-right: 29px;">
            <v-list-item-avatar
              color="pink"
              class="justify-center mr-0 my-0"
              size="30"
              style="margin-left: -3px;"
            >
              <v-icon color="white" small>mdi-calendar-multiple</v-icon>
            </v-list-item-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <span>{{ bookingGroup.booking_number }}</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-show="hasRemarks"
                    aria-hidden="false"
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-tag-text-outline
                  </v-icon>
                </template>
                <div style="padding: 4px;">
                  <h3>Anteckningar:</h3>
                  <i>{{ bookingGroup.remarks }}</i>
                </div>
              </v-tooltip>
            </v-list-item-title>
            <v-list-item-subtitle style="width: auto;">Bokningsnummer gruppbokning</v-list-item-subtitle>
          </v-list-item-content>

        </v-list-item>
      </template>
    </template>

    <template v-if="!hiddenSections.includes('customer')">
      <template v-if="isLoading">
        <div
          aria-busy="true"
          aria-live="polite"
          role="alert"
          class="v-skeleton-loader v-skeleton-loader--is-loading theme--light"
          style="max-width: 300px;"
        >
          <div class="v-skeleton-loader__list-item-avatar v-skeleton-loader__bone">
            <div class="v-skeleton-loader__avatar v-skeleton-loader__bone"></div>
            <div style="margin-top: -8px; width: 160px; max-width: 160px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
            <div style="position: absolute; left: 72px; top: 32px; width: 212px; height: 8px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <v-list-item @click.native="openCustomer" :ripple="customerClickable" :class="{ 'cursor-pointer highlight-on-hover': customerClickable }">
          <v-list-item-icon style="margin-right: 29px;">
            <customer-avatar :customer="bookingGroup.customer" style="margin-left: -3px;"></customer-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title v-text="bookingGroup.customer.full_name"></v-list-item-title>
            <v-list-item-subtitle v-text="bookingGroup.customer.shortAddress"></v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </template>

    <template v-if="!hiddenSections.includes('dates') && hasBookings">
      <template v-if="isLoading">
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-arrow-right</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <v-skeleton-loader
                type="list-item"
                class="skeleton-loader-title-with-subtitle"
              ></v-skeleton-loader>
            </v-list-item-title>
            <v-list-item-subtitle>Incheckning</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-arrow-left</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <v-skeleton-loader
                type="list-item"
                class="skeleton-loader-title-with-subtitle"
              ></v-skeleton-loader>
            </v-list-item-title>
            <v-list-item-subtitle>Utcheckning</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-arrow-right</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <span>{{ checkInDateFormatted }}</span>
              <v-chip
                v-if="isDeleted"
                class="ml-2"
                small
                color="error"
                text-color="white"
              >
                Avbokad
              </v-chip>
              <v-chip
                v-else-if="!!checkinStatus"
                class="ml-2 checked-in-badge"
                small
                :color="checkinStatus"
                text-color="white"
              >
                <v-icon left :small="checkinStatus === 'warning'">
                  {{ checkinStatus === 'success' ? 'mdi-check' : 'mdi-clock' }}
                </v-icon>
                {{ checkinPillText }}
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle>Incheckning</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-arrow-left</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              {{ checkOutDateFormatted }}
              <v-chip
                v-if="!!checkoutStatus"
                class="ml-2 checked-out-badge"
                small
                :color="checkoutStatus"
                text-color="white"
              >
                <v-icon left :small="checkoutStatus === 'warning'">
                  {{ checkoutStatus === 'success' ? 'mdi-check' : 'mdi-clock' }}
                </v-icon>
                {{ checkoutPillText }}
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle>Utcheckning</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </template>

    <template v-if="!hiddenSections.includes('created-at')">
      <template v-if="isLoading">
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <v-skeleton-loader
                type="list-item"
                class="skeleton-loader-title-with-subtitle"
              ></v-skeleton-loader>
            </v-list-item-title>
            <v-list-item-subtitle>Gruppbokning mottagen</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item>
          <v-list-item-icon class="my-auto">
            <v-icon>mdi-calendar-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              {{ bookingCreatedAtFormatted }}
            </v-list-item-title>
            <v-list-item-subtitle>Gruppbokning mottagen</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </template>

    <template v-if="!isLoading && !hasBookings">
      <v-alert
        type="info"
        text
        class="mt-4"
      >
        Det finns inte några bokningar kvar på denna gruppbokning. Det kan bero på att bokningarna har flyttats till en annan gruppbokning.
      </v-alert>
    </template>
  </v-list>
</template>

<script>
import CustomerAvatar from '@/components/customer/Avatar.vue'

export default {
  name: 'BookingGroupDataList',
  components: {
    CustomerAvatar
  },
  props: {
    bookingGroup: Object,
    isLoading: Boolean,
    hiddenSections: {
      type: Array,
      default: function () { return [] } // booking-group, customer, dates, created-at
    },
    customerClickable: Boolean,
    bookingGroupClickable: Boolean,
    insideDialog: Boolean
  },
  data: () => ({}),
  computed: {
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    isDeleted: function () {
      return this.bookingGroup.deleted_at != null
    },
    hasBookings: function () {
      // Kan hända att en gruppbokning inte har några bokningar kvar om bokningarna flyttats till en annan gruppbokning
      return this.bookingGroup?.bookings?.length > 0
    },
    checkOutDateFormatted: function () {
      if (this.bookingGroup.has_mixed_checkout_dates) {
        const checkoutDates = this.bookingGroup.checkout_dates.map((date, i) => date.toHumanReadableDate({ capitalized: i === 0 }))
        return checkoutDates.join(', ')
      }
      return this.bookingGroup.last_checkout_date.toHumanReadableDate()
    },
    checkInDateFormatted: function () {
      if (this.bookingGroup.has_mixed_checkin_dates) {
        const checkinDates = this.bookingGroup.checkin_dates.map((date, i) => date.toHumanReadableDate({ capitalized: i === 0 }))
        return checkinDates.join(', ')
      }
      return this.bookingGroup.first_checkin_date.toHumanReadableDate()
    },
    isDelayedCheckout: function () {
      const today = window.dayjs.today()
      return this.bookingGroup.bookings.some(booking => booking.checked_out_at === null && booking.deleted_at === null && booking.check_out.isBefore(today))
    },
    isDelayedCheckin: function () {
      const today = window.dayjs.today()
      return this.bookingGroup.bookings.some(booking => booking.checked_in_at === null && booking.deleted_at === null && booking.check_in.isBefore(today))
    },
    checkinStatus: function () {
      // success, warning, null
      if (this.bookingGroup.bookings.length === 0) {
        return null
      }
      // någon bokning är försenad => warning
      if (this.isDelayedCheckin) {
        return 'warning'
      }
      // någon bokning är incheckad => success
      const hasCheckedIn = this.bookingGroup.bookings.some(booking => booking.checked_in_at != null)
      if (hasCheckedIn) {
        return 'success'
      }
      return null
    },
    checkoutStatus: function () {
      // success, warning, null
      if (this.bookingGroup.bookings.length === 0) {
        return null
      }
      // någon bokning är försenad => warning
      if (this.isDelayedCheckout) {
        return 'warning'
      }
      // någon bokning är utcheckad => success
      const hasCheckedOut = this.bookingGroup.bookings.some(booking => booking.checked_out_at != null)
      if (hasCheckedOut) {
        return 'success'
      }
      return null
    },
    checkinPillText: function () {
      if (this.bookingGroup.bookings.length === 0 || this.isDeleted) {
        return null
      }
      if (this.isDelayedCheckin) {
        return 'Försenad incheckning'
      }
      if (this.bookingGroup.bookings.every(booking => booking.checked_in_at != null || booking.deleted_at != null)) {
        return 'Alla incheckade'
      }
      if (this.bookingGroup.bookings.some(booking => booking.checked_in_at != null)) {
        return 'Vissa incheckade'
      }
      return null
    },
    checkoutPillText: function () {
      if (this.bookingGroup.bookings.length === 0 || this.isDeleted) {
        return null
      }
      if (this.isDelayedCheckout) {
        return 'Försenad utcheckning'
      }
      if (this.bookingGroup.bookings.every(booking => booking.checked_out_at != null || booking.deleted_at != null)) {
        return 'Alla utcheckade'
      }
      if (this.bookingGroup.bookings.some(booking => booking.checked_out_at != null)) {
        return 'Vissa utcheckade'
      }
      return null
    },
    bookingCreatedAtFormatted: function () {
      if (this.bookingGroup.created_at && this.bookingGroup.channel) {
        const time = this.bookingGroup.created_at.toHumanReadableDate()
        return `${time} från ${this.bookingGroup.channel.readable_name}`
      }
      return ''
    },
    hasRemarks: function () {
      return this.bookingGroup && !!this.bookingGroup.remarks
    }
  },
  methods: {
    openCustomer: function () {
      if (this.customerClickable) {
        this.$store.dispatch('customer/openDialogById', { customerId: this.bookingGroup.customer_id, smaller: this.insideDialog && !this.isMobile })
      }
    },
    openBookingGroup: function () {
      if (this.bookingGroupClickable) {
        this.$store.dispatch('bookingGroup/openDialogById', { bookingGroupId: this.bookingGroup.id })
      }
    }
  }
}
</script>
